import Button from '@/components/Button/Button';
import AudioDescriptionIcon from '@/components/Icon/AudioDescriptionIcon';
import PauseIcon from '@/components/Icon/PauseIcon';
import PlayIcon from '@/components/Icon/PlayIcon';
import IconButton from '@/components/IconButton/IconButton';
import useIsNative from '@/hooks/native/useIsNative';
import useNativeMessages from '@/hooks/native/useNativeMessages';
import useIsAudioDescriptionPreferred from '@/hooks/useIsAudioDescriptionPreferred';
import useVideo from '@/hooks/useVideo';
import { Video } from '@/lib/apiTypes';
import cn from '@/lib/cs';
import {
	AudioDescriptionVideoPlayer,
	AudioDescriptionVideoPlayerProps,
	AudioDescriptionVideoPlayerRef,
} from '@ntrinternet/ntr-ui';
import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';

const VideoPlayer = forwardRef<
	AudioDescriptionVideoPlayerRef,
	{
		video: Video;
		isAutoPlay?: boolean;
		showThumbnail?: boolean;
		onIsPlayingChange?: AudioDescriptionVideoPlayerProps['onIsPlayingChange'];
		onCurrentTimeChange?: AudioDescriptionVideoPlayerProps['onCurrentTimeChange'];
		onEnded?: () => void;
	}
>(
	(
		{
			video,
			isAutoPlay = false,
			showThumbnail = false,
			onIsPlayingChange,
			onCurrentTimeChange,
			onEnded,
		},
		ref,
	) => {
		// Native connection
		const isNative = useIsNative();
		const { sendNativeMessage, awaitSingleNativeMessage } =
			useNativeMessages();

		// Load audio description and subtitles
		const { audioDescriptionFile, subtitlesBase64 } = useVideo({
			video,
		});

		// Video player ref
		const videoRef = useRef<AudioDescriptionVideoPlayerRef>(null);
		useImperativeHandle(
			ref,
			() => videoRef.current as AudioDescriptionVideoPlayerRef,
		);

		// State
		const [isPlaying, setIsPlaying] = useState(isAutoPlay);
		const [isPlayingAudioDescription, setIsPlayingAudioDescription] =
			useState(false);
		const { isAudioDescriptionPreferred, setIsAudioDescriptionPreferred } =
			useIsAudioDescriptionPreferred();
		const [isVideoLoadError, setIsVideoLoadError] = useState(false);

		useEffect(() => {
			if (isVideoLoadError)
				alert('Video kon niet goed worden geladen. Ben je online?');
		}, [isVideoLoadError]);

		useEffect(() => {
			if (onIsPlayingChange) onIsPlayingChange(isPlaying);
		}, [isPlaying, onIsPlayingChange]);

		return (
			<section
				aria-label={`Video: ${video.title}`}
				className="max-w-screen relative size-full max-h-dvh overflow-hidden">
				<AudioDescriptionVideoPlayer
					ref={videoRef}
					playsInline
					isPlaying={isPlaying}
					src={video.video.data?.attributes.url}
					audioDescription={audioDescriptionFile}
					isAudioDescriptionEnabled={isAudioDescriptionPreferred}
					isCustomVoiceEnabled={isNative}
					onCustomVoiceStart={(line, onEnd) => {
						sendNativeMessage('tts.speak', line.text);
						awaitSingleNativeMessage('tts.spoken').then(onEnd);
					}}
					className={cn(
						'max-w-screen size-full max-h-dvh object-contain',
					)}
					style={{
						backgroundImage: showThumbnail
							? `url(${video.thumbnail.data?.attributes.url})`
							: undefined,
						backgroundSize: 'cover',
						backgroundPosition: 'center',
					}}
					onClick={() => setIsPlaying((prev) => !prev)}
					onEnded={() => {
						videoRef.current?.manuallyUpdateCurrentTime(0);
						setIsPlaying(false);
						onEnded?.();
					}}
					onIsPlayingChange={(isPlaying) => {
						setIsPlaying(isPlaying);
					}}
					onIsPlayingAudioDescriptionChange={
						setIsPlayingAudioDescription
					}
					onCurrentTimeChange={onCurrentTimeChange}
					onError={() => setIsVideoLoadError(true)}>
					{subtitlesBase64.length > 0 && (
						<track
							src={subtitlesBase64}
							label="Nederlands"
							kind="subtitles"
							srcLang="nl"
							default
						/>
					)}
				</AudioDescriptionVideoPlayer>
				{audioDescriptionFile.audioDescription.length > 0 && (
					<Button
						className="absolute end-4 top-4"
						role="switch"
						aria-label={
							isAudioDescriptionPreferred
								? 'Audiodescriptie aanzetten'
								: 'Audiodescriptie uitzetten'
						}
						disabled={isPlayingAudioDescription}
						aria-checked={isAudioDescriptionPreferred}
						label={isAudioDescriptionPreferred ? 'Aan' : 'Uit'}
						backgroundClassName={
							isAudioDescriptionPreferred
								? 'text-accent'
								: undefined
						}
						textClassName={
							isAudioDescriptionPreferred
								? 'text-accentCompat'
								: undefined
						}
						icon={<AudioDescriptionIcon className="scale-150" />}
						onClick={() => {
							videoRef.current?.manuallyUpdateCurrentTime(0);
							setIsAudioDescriptionPreferred(
								!isAudioDescriptionPreferred,
							);
						}}
					/>
				)}
				<div
					className={`absolute transition-all duration-300 ${
						(isPlaying ?? true)
							? 'start-2 top-2 scale-100'
							: 'start-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 scale-150'
					}`}>
					<IconButton
						icon={isPlaying ? <PauseIcon /> : <PlayIcon />}
						onClick={() => {
							if (isPlaying) {
								setIsPlaying(false);
								// If video is less than 16 seconds, reset to beginning
								if (
									videoRef.current?.videoElement?.duration ??
									0 < 16
								)
									videoRef.current?.manuallyUpdateCurrentTime(
										0,
									);
							} else {
								setIsPlaying(true);
							}
						}}
						aria-label={isPlaying ? 'Pauzeren' : 'Afspelen'}
						title={isPlaying ? 'Video pauzeren' : 'Video afspelen'}
						variant={isPlaying ? undefined : 'accent'}
						aria-disabled={isPlayingAudioDescription}
						disabled={isPlayingAudioDescription}
					/>
				</div>
			</section>
		);
	},
);

export default VideoPlayer;
